"use client";
import { PropsWithChildren } from "react";
import { useRef, useEffect } from "react";
import { SwiperContainer, register } from "swiper/element/bundle";
import { Pagination } from "swiper/modules";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ["swiper-container"]: any;
    }
  }
}

export default function Slider({ children }: PropsWithChildren<{}>) {
  const swiperRef = useRef<SwiperContainer>(null);

  const initializeSwiper = (swiperContainer: SwiperContainer) => {
    register();

    const params = {
      observeParents: true,
      observer: true,
      slidesPerView: "auto",
      centeredSlides: true,
      centerInsufficientSlides: false,
      slideToClickedSlide: true,
      initialSlide: 0,
      spaceBetween: 24,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 20,
      pagination: { clickable: true },
      modules: [Pagination],
      injectStyles: [
        `
            .swiper .swiper-pagination {
              all: unset;
              display: flex;
              align-items: center;
              column-gap: 14px;
              position: relative;
              top: unset;
              max-width: max-content;
              z-index: 450;
              margin: 10px auto 0px auto;
              padding: 20px 20px;
            }
    
            .swiper .swiper-pagination .swiper-pagination-bullet {
              margin: 0;
              width: min(max(16px, 1.5vw), 18px);
              height: min(max(16px, 1.5vw), 18px);
              border-radius: 100%;
              background: var(--color-light-grey);
              opacity: 1;
              cursor: pointer;
              transition: .2s ease all;
              position: relative;
            }
    
            .swiper .swiper-pagination .swiper-pagination-bullet:before {
              content: "";
              position: absolute;
              inset: 0px;
              margin: auto auto;
              width: calc(150%);
              height: calc(150%);
              z-index: -1;
              left: -25%;
            }
    
            .swiper .swiper-pagination .swiper-pagination-bullet.swiper-pagination-bullet-active {
              background: var(--color-primary);
            }
    
            .swiper .swiper-pagination .swiper-pagination-bullet:hover:not(.swiper-pagination-bullet-active) {
              background: var(--color-primary);
              opacity: 0.75;
            }
    
            `,
      ],

      breakpoints: {
        767: {
          slidesPerView: "auto",
          slidesOffsetBefore: 20,
          slidesOffsetAfter: 20,
        },
        990: {
          slidesPerView: "auto",
          centeredSlides: false,
          spaceBetween: 50,
          slidesOffsetBefore: 34,
          slidesOffsetAfter: 34,
        },
        1410: {
          slidesPerView: "auto",
          centeredSlides: true,
          centerInsufficientSlides: false,
          spaceBetween: 50,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 34,
        },
      },
    };

    Object.assign(swiperContainer, params);
    swiperContainer.initialize();
  };

  useEffect(() => {
    if (swiperRef.current !== null) {
      initializeSwiper(swiperRef.current);
    }
  }, []);

  return (
    <swiper-container pagination={true} ref={swiperRef} init={false}>
      {children}
    </swiper-container>
  );
}
